import type { CoreError } from '@ingenico/api-sdk-core/common';
import type { LockScreenRequestApi } from '@ingenico/api-sdk-device-management-public-v2';
import { Level, showNotification } from '@ingenico/react-notifications';
import { sectionContainerAtom, useBusinessService } from '@ingenico/react-section-utils';
import { requestAndUpdateState } from '@ingenico/react-utils/functions';
import { useAsyncDataState } from '@ingenico/react-utils/hooks';
import type { LogicalDeviceBlockDeviceModalProps } from '@ingenico/type-mfe-contracts/domains';
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import deepEqual from 'fast-deep-equal';
import { createStore, Provider } from 'jotai';
import { toString } from 'lodash-es';
import type { ReactElement } from 'react';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { deviceManagementContainer } from '../../container.const';
import { DeviceManagementService } from '../../domain/business/services/deviceManagement.service';
import { useDeviceManagementError } from '../../hooks';
import { BlockDeviceModal } from '../block-device-modal/BlockDeviceModal.ctrl';
import { UnblockDeviceModal } from '../unblock-device-modal/UnblockDeviceModal.ctrl';

function ExposedLogicalDeviceBlockDeviceModalContent({
  isOpen,
  mode,
  onClose,
  onSuccess,
  physicalDeviceId,
}: LogicalDeviceBlockDeviceModalProps): ReactElement {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.LOGICAL_DEVICE]);
  const { getErrorMessage } = useDeviceManagementError();
  const deviceManagementBusinessService = useBusinessService(DeviceManagementService);
  const [blockDeviceRequestState, setBlockDeviceRequestState] = useAsyncDataState<unknown, CoreError>();

  const handleClose = useCallback(() => {
    setBlockDeviceRequestState({
      isLoading: false,
      isError: false,
      isSuccess: false,
      error: null,
      data: undefined,
    });
    onClose();
  }, [onClose, setBlockDeviceRequestState]);

  const handleBlockDevice = useCallback(
    (data: LockScreenRequestApi): Promise<void> =>
      new Promise((resolve, reject) => {
        requestAndUpdateState(
          deviceManagementBusinessService.blockDeviceScreen(physicalDeviceId, data),
          setBlockDeviceRequestState,
          {
            onSuccess: () => {
              showNotification({
                content: toString(t('success.blockDevice')),
                level: Level.Success,
              });
              handleClose();
              onSuccess?.();
              resolve(void 0);
            },
            onError: (error) => {
              showNotification({ content: getErrorMessage(error), level: Level.Error });
              reject(error);
            },
          },
        );
      }),
    [
      deviceManagementBusinessService,
      getErrorMessage,
      handleClose,
      onSuccess,
      physicalDeviceId,
      setBlockDeviceRequestState,
      t,
    ],
  );

  const handleUnblockDevice = useCallback(
    (): Promise<void> =>
      new Promise((resolve, reject) => {
        requestAndUpdateState(
          deviceManagementBusinessService.unblockDeviceScreen(physicalDeviceId),
          setBlockDeviceRequestState,
          {
            onSuccess: () => {
              showNotification({
                content: toString(t('success.unblockDevice')),
                level: Level.Success,
              });
              handleClose();
              onSuccess?.();
              resolve(void 0);
            },
            onError: (error) => {
              showNotification({ content: getErrorMessage(error), level: Level.Error });
              reject(error);
            },
          },
        );
      }),
    [
      deviceManagementBusinessService,
      getErrorMessage,
      handleClose,
      onSuccess,
      physicalDeviceId,
      setBlockDeviceRequestState,
      t,
    ],
  );

  return mode === 'block' ? (
    <BlockDeviceModal
      isOpen={isOpen}
      isLoading={blockDeviceRequestState.isLoading}
      onBlockDevice={handleBlockDevice}
      onCancel={handleClose}
    />
  ) : (
    <UnblockDeviceModal
      isOpen={isOpen}
      isLoading={blockDeviceRequestState.isLoading}
      onUnblockDevice={handleUnblockDevice}
      onCancel={handleClose}
    />
  );
}

function ExposedLogicalDeviceBlockDeviceModalCpnt(props: LogicalDeviceBlockDeviceModalProps): ReactElement {
  // Pre-initialize the section container on a fresh store before any child renders.
  // @ingenico/react-section-utils reads sectionContainerAtom synchronously during render and
  // throws if it is null, so the value must be set before the Provider mounts its children.
  const store = useMemo(() => {
    const sectionStore = createStore();
    sectionStore.set(sectionContainerAtom, deviceManagementContainer);
    return sectionStore;
  }, []);

  return (
    <Provider store={store}>
      <ExposedLogicalDeviceBlockDeviceModalContent {...props} />
    </Provider>
  );
}

export const ExposedLogicalDeviceBlockDeviceModal = memo(ExposedLogicalDeviceBlockDeviceModalCpnt, deepEqual);
